import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useFeature } from '@growthbook/growthbook-react'

import usePageModal from 'features/Modal/hooks/usePageModal'
import { logEvent } from 'lib/analytics'
import { toBooleanString } from 'lib/helpers'
import { selectTimelineFilters, setFilters } from 'store/slices/timeline'
import { addNotification } from 'store/slices/notifications'
import { Link, useNavigate } from 'features/i18n/router'
import { FavFilterGrowthBookEnabledType } from 'features/FeatureFlag'
import { useLogEventTimelineFilter } from './useLogEventTimelineFilter'
import { selectIsLoggedIn } from 'store/selectors'
import { MODAL_MODE } from '../config'
import {
  useGetSettingQuery,
  useUpdateSettingMutation,
} from 'store/api/yay/user'

import Button from 'components/Button'
import { SettingItem, Switch } from 'components'

const TimelineFilterAnalyticsKey = {
  hideHotPost: 'hide_hot_posts',
  sharedInterests: 'shared_interests',
} as const

const TimelineFilterModal = ({ location }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { generatePageModalUrl } = usePageModal()
  const navigate = useNavigate()
  const isFavFilterGrowthBookEnabled =
    useFeature<FavFilterGrowthBookEnabledType>('yay-13642')
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const getSettingsRes = useGetSettingQuery(null, {
    skip: !isLoggedIn,
  })

  const [updateSetting, updateSettingRes] = useUpdateSettingMutation()

  const filters = useSelector(selectTimelineFilters)
  const [localFilters, setLocalFilters] = useState(filters)

  const getFilterProps = (name: keyof typeof filters) => ({
    name,
    checked: localFilters[name],
    onChange: (c: boolean) => {
      logEvent.timeline_filter_update({
        [TimelineFilterAnalyticsKey[name]]: toBooleanString(c),
        origin: 'timeline',
      })

      setLocalFilters((prev) => ({ ...prev, [name]: c }))
    },
  })

  const getSettingsProps = (mode: string) => ({
    checked: getSettingsRes?.data?.setting?.[mode],
    disabled: getSettingsRes?.isFetching || updateSettingRes?.isLoading,
    onChange: (on: boolean) => {
      updateSetting({
        mode,
        on,
      })
    },
  })

  useLogEventTimelineFilter()

  return (
    <div className="Modal__wrapper Modal__wrapper--auto-h TimelineFilterModal">
      <div className="Modal__header">
        <Link className="Modal__header__close" to={location.pathname} />

        <h2 className="Modal__header__h2">
          {t('timeline_page.timeline_filter')}
        </h2>

        <div className="Modal__header__right">
          <Button
            buttonStyle="secondary"
            onClick={() => {
              dispatch(setFilters(localFilters))
              dispatch(
                addNotification({
                  message: t('timeline_page.timeline_filter.success_changed'),
                })
              )
              navigate(location.pathname)
            }}
          >
            {t('common.apply')}
          </Button>
        </div>
      </div>

      <div className="Modal__body">
        <h3 className="TextSectionTitle">
          {t('timeline_page.timeline_filter.display_settings')}
        </h3>

        <SettingItem
          label={t('timeline_page.timeline_filter.hide_hot_post')}
          sub={t('timeline_page.timeline_filter.hide_hot_post_description')}
        >
          <Switch {...getFilterProps('hideHotPost')} />
        </SettingItem>

        <SettingItem
          label={t('timeline_page.timeline_filter.public_no_reply_mode')}
          sub={t(
            'timeline_page.timeline_filter.public_no_reply_mode_description'
          )}
        >
          <Switch {...getSettingsProps('hide_reply_public_timeline')} />
        </SettingItem>

        <SettingItem
          label={t('timeline_page.timeline_filter.following_no_reply_mode')}
          sub={t(
            'timeline_page.timeline_filter.public_no_reply_mode_description'
          )}
        >
          <Switch {...getSettingsProps('hide_reply_following_timeline')} />
        </SettingItem>

        <h3 className="TextSectionTitle">
          {t('timeline_page.timeline_filter.filters')}
        </h3>

        <SettingItem
          label={t('keyword_filter_page.title')}
          sub={t('timeline_page.timeline_filter.keyword.description')}
        >
          <Link to={generatePageModalUrl(MODAL_MODE.MUTE_KEYWORDS)}>
            <img src="/images/icon-right.svg" />
          </Link>
        </SettingItem>

        {isFavFilterGrowthBookEnabled?.value == 'user_adjustable' && (
          <SettingItem
            label={t('timeline_page.timeline_filter.faves')}
            sub={
              <Trans i18nKey="timeline_page.timeline_filter.faves_description">
                <Link
                  to={generatePageModalUrl(MODAL_MODE.INTERESTS_SELECTION, {
                    returnPath: location.pathname + location.search,
                  })}
                />
              </Trans>
            }
          >
            <Switch {...getFilterProps('sharedInterests')} />
          </SettingItem>
        )}
      </div>
    </div>
  )
}

export default TimelineFilterModal
